import { type FC, memo } from "react";
import { Link, LinkKind, Text } from "@stubhub/broadway-web";
import type { EventListGridItem } from "src/http/api-types";
import { FormatContent, useContent, useFormattedContent } from "src/shared/components/content";
import { SeoTable, type SeoTableColumn } from "src/website-apps/shared/components/seo-table/SeoTable";

interface Props {
  rows: readonly EventListGridItem[];
  performerName: string;
}

export const PerformerEventTable: FC<Props> = memo(({ rows, performerName }) => {
  /** "Event" */
  const eventHeader = useContent("cid$#4374580#");
  /** "Location" */
  const locationHeader = useContent("cid$#4374392#");
  /** "Venue" */
  const venueHeader = useContent("cid$#4374393#");
  /** "TBA" */
  const tbaLabel = useContent("cid$#3656911#");
  // "{0} Event Dates"
  const headingText = useFormattedContent("cid$#4374394#", [performerName]);

  const columns: SeoTableColumn<EventListGridItem>[] = [
    {
      header: eventHeader,
      renderCell: (event) => {
        const dateText = event.isDateConfirmed ? (event.formattedDate ?? tbaLabel) : tbaLabel;
        return (
          <Link href={event.url ?? "#"} kind={LinkKind.Ghost}>
            <Text variant="body" size="small" as="span">
              {/* "{0} event {1}" */}
              <FormatContent id="cid$#4374581#" params={[performerName, dateText]} />
            </Text>
          </Link>
        );
      },
    },
    {
      header: locationHeader,
      renderCell: (event) =>
        event.cityUrl ? (
          <Link href={event.cityUrl} kind={LinkKind.Ghost}>
            <Text variant="body" size="small" as="span">
              {event.formattedVenueLocation ?? ""}
            </Text>
          </Link>
        ) : (
          <Text variant="body" size="small" as="span">
            {event.formattedVenueLocation ?? ""}
          </Text>
        ),
    },
    {
      header: venueHeader,
      renderCell: (event) =>
        event.venueUrl ? (
          <Link href={event.venueUrl} kind={LinkKind.Ghost}>
            <Text variant="body" size="small" as="span">
              {event.venueName ?? ""}
            </Text>
          </Link>
        ) : (
          <Text variant="body" size="small" as="span">
            {event.venueName ?? ""}
          </Text>
        ),
    },
  ];

  return (
    <SeoTable
      rows={rows}
      heading={headingText}
      caption={headingText}
      columns={columns}
      rowKey={(event) => event.eventId}
    />
  );
});
PerformerEventTable.displayName = "PerformerEventTable";
